import type { useNumberFormatter } from '@mint-lib/formatting-hooks';

import { CHART_ITEM_LENGTH } from '../constants/charts.js';
import type {
  DataEntry,
  GroupedEntry,
  ScatterplotDomain,
} from '../types/charts.js';

export const CHART_COLORS_IN_ORDER_TAILWIND = [
  'dataVisualization-blue400',
  'dataVisualization-red400',
  'dataVisualization-fuchsia400',
  'dataVisualization-orange400',
  'dataVisualization-emerald400',
  'dataVisualization-yellow400',
  'dataVisualization-violet400',
  'dataVisualization-lime400',
  'dataVisualization-blue600',
  'dataVisualization-fuchsia600',
  'dataVisualization-orange600',
  'dataVisualization-emerald600',
  'dataVisualization-yellow600',
  'dataVisualization-violet600',
  'dataVisualization-red600',
  'dataVisualization-lime600',
  'dataVisualization-blue200',
  'dataVisualization-fuchsia200',
  'dataVisualization-orange200',
  'dataVisualization-emerald200',
  'dataVisualization-yellow200',
  'dataVisualization-violet200',
  'dataVisualization-red200',
  'dataVisualization-lime200',
  'dataVisualization-blue800',
  'dataVisualization-fuchsia800',
  'dataVisualization-orange800',
  'dataVisualization-emerald800',
  'dataVisualization-yellow800',
  'dataVisualization-violet800',
  'dataVisualization-lime800',
  'dataVisualization-red800',
];

export const adjustSaturation = (color: string, factor: number) => {
  const hex = colorToHex(color);

  let r = Number.parseInt(hex.slice(1, 3), 16);
  let g = Number.parseInt(hex.slice(3, 5), 16);
  let b = Number.parseInt(hex.slice(5, 7), 16);

  r = Math.floor(r + (255 - r) * factor);
  g = Math.floor(g + (255 - g) * factor);
  b = Math.floor(b + (255 - b) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const colorToHex = (color: string) => {
  return color.startsWith('dataVisualization-')
    ? getComputedStyle(document.documentElement)
        .getPropertyValue(`--${color}`)
        .trim()
    : color;
};

export const colorizeData = (
  dataframe: DataEntry[],
  colors: string[],
  label?: string,
): GroupedEntry[] => {
  return dataframe.map((item, i) => ({
    ...item,
    label: dataframe[i][label ?? ''] ?? '',
    fill: colorToHex(colors[i % colors.length]),
  }));
};

export const getChartFullWidth = (length: number) => {
  return Math.max(700, length * CHART_ITEM_LENGTH);
};

export const snakeCaseToLabel = (str: string) => {
  return str
    .split('_')
    .map((word) => {
      if (hasAbbreviation(word)) {
        return word;
      }

      return capitalize(word.toLowerCase());
    })
    .join(' ');
};

const hasAbbreviation = (word: string) =>
  /^[^A-Za-z]*[A-Z]{2,}[^A-Za-z]*$/.test(word); // FYI: Does not detect mixed-case abbreviations like 'WoW'

export const pascalCaseToLabel = (str: string) => {
  const words = str
    .replaceAll(/([A-Z])(?=[A-Z][a-z])/g, '$1 ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll(/([A-Za-z])(\d)/g, '$1 $2')
    .replaceAll(/(\d)([A-Za-z])/g, '$1 $2')
    .trim()
    .split(/\s+/);

  return words
    .map((word) => {
      if (hasAbbreviation(word)) {
        return word;
      }

      return word.toLowerCase().replace(/[a-z]/, (char) => char.toUpperCase()); // Capitalize alternative, ignoring brackets
    })
    .join(' ');
};

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatLabel = ({ label }: { label: string }) => {
  if (label.includes('_')) {
    return snakeCaseToLabel(label);
  }
  return pascalCaseToLabel(label);
};

export const formatChartValue = ({
  numberFormatter,
  value,
}: {
  numberFormatter: ReturnType<typeof useNumberFormatter>;
  value: unknown;
}) => {
  if (typeof value === 'string') {
    const number = Number.parseFloat(value);
    return Number.isFinite(number)
      ? numberFormatter.format(value, {
          options: {
            maxDecimals: 2,
          },
        })?.formatted
      : value;
  }

  if (typeof value === 'number') {
    return numberFormatter.format(value, {
      options: {
        maxDecimals: 2,
      },
    })?.formatted;
  }

  return '-';
};

export const truncateLabel = (label: string, maxLength: number) => {
  return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
};

export const computeScatterDomain = (
  dataframe: DataEntry[],
  key: string,
  minMul = 1,
  maxMul = 1,
): ScatterplotDomain => {
  const values = dataframe
    .map((d) => Number(d[key]))
    .filter((v) => !Number.isNaN(v));

  if (values.length !== dataframe.length) return ['auto', 'auto'];

  const min = Math.min(...values) * minMul;
  const max = Math.max(...values) * maxMul;

  return [min, max];
};
